import React, { useCallback, useContext, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, XCircle } from "lucide-react";
import AppContext from "../../context/AppContext";
import CommonButton from "./CommonButton";
import ModalCloseButton from "./ModalCloseButton";
import translations from "./Translations";
import { dirForLang } from "../../utils/rtl";

const variantStyles = {
  warning: {
    icon: AlertTriangle,
    iconWrapper: "bg-amber-50 text-[#FF9F29]",
    button: "warning",
  },
  error: {
    icon: XCircle,
    iconWrapper: "bg-red-50 text-[#FF0000]",
    button: "danger",
  },
  success: {
    icon: CheckCircle2,
    iconWrapper: "bg-emerald-50 text-[#45B369]",
    button: "success",
  },
  info: {
    icon: Info,
    iconWrapper: "bg-blue-50 text-[#236DB4]",
    button: "primary",
  },
};

/**
 * Common alert dialog: message + a single centered "Ok" button.
 * Use directly, or through `useAlertModal` for the show/close plumbing.
 */
const AlertModal = ({
  open,
  title,
  message,
  onClose,
  okText,
  variant = "warning",
  showCloseIcon = true,
  dataTestId = "alert-modal",
}) => {
  const { theme, langMode } = useContext(AppContext);
  const styles = variantStyles[variant] ?? variantStyles.warning;
  const Icon = styles.icon;

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      dir={dirForLang(langMode)}
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alert-modal-title"
      onMouseDown={(e) => e.stopPropagation()}
      data-testid={dataTestId}
    >
      <div
        className={`relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl transition-all duration-300 ease-out ${
          theme === "dark"
            ? "dark-mode border border-slate-500"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
        }`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {showCloseIcon && (
          <ModalCloseButton
            onClick={onClose}
            className="absolute top-3 ltr:right-3 rtl:left-3"
          />
        )}

        <div className="flex flex-col items-center pt-2 text-center">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full ${styles.iconWrapper}`}
          >
            <Icon size={24} strokeWidth={2.2} />
          </span>
          {title && (
            <h3
              id="alert-modal-title"
              className="mt-4 text-lg font-semibold text-slate-800"
              data-testid={`${dataTestId}-title`}
            >
              {title}
            </h3>
          )}
          {message && (
            <p
              className="mt-2 px-2 text-sm leading-relaxed text-slate-600"
              data-testid={`${dataTestId}-message`}
            >
              {message}
            </p>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <CommonButton colorIntent={styles.button}
            onClick={onClose}
            variant={styles.button}
            theme={theme}
            className="min-w-[104px] rounded-lg"
            data-testid={`${dataTestId}-ok-button`}
          >
            {okText || translations[langMode].ok}
          </CommonButton>
        </div>
      </div>
    </div>
  );
};

/**
 * Small helper so screens only need `showAlert({ title, message })`:
 *
 *   const { alertProps, showAlert } = useAlertModal();
 *   showAlert({ title: "...", message: "...", variant: "warning" });
 *   <AlertModal {...alertProps} />
 */
export const useAlertModal = () => {
  const [alertState, setAlertState] = useState({ open: false });

  const showAlert = useCallback((options = {}) => {
    const config = typeof options === "string" ? { message: options } : options;
    setAlertState({ ...config, open: true });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    showAlert,
    closeAlert,
    alertProps: { ...alertState, onClose: closeAlert },
  };
};

export default AlertModal;
